import React, { useCallback, useState } from "react";

import { DriverTermsAndConditionsType } from "@daytrip/legacy-enums";
import { observer } from "mobx-react";

import { Button } from "@components/Button/Button";
import { Container } from "@components/layout/Container/Container";
import { Headline } from "@components/typography/Headline/Headline";

import { useAcceptDriverTermsAndConditions } from "../../hooks/useAcceptDriverTermsAndConditions";
import { useDriverTermsAndConditionsRequiringAcceptance } from "../../hooks/useDriverTermsAndConditionsRequiringAcceptance";
import { DriverTermsAndConditionsModal } from "../DriverTermsAndConditionsModal/DriverTermsAndConditionsModal";

export const AcceptDriverTermsAndConditionsOverlay = observer(() => {
    const [selectedTermsType, setSelectedTermsType] = useState<DriverTermsAndConditionsType | null>(null);

    const {
        data: termsAndConditionsForAcceptance,
        refetch,
        isLoading: isFetching,
    } = useDriverTermsAndConditionsRequiringAcceptance();
    const { mutateAsync: acceptTermsAndConditions, isLoading: isAccepting } = useAcceptDriverTermsAndConditions();
    const isLoading = isFetching || isAccepting;

    const changelog = termsAndConditionsForAcceptance?.changelog ?? [];
    const hasTermsAndConditionsChangelog = changelog.some(
        (item) => item.type === DriverTermsAndConditionsType.TermsAndConditions,
    );
    const hasPrivacyPolicyChangelog = changelog.some(
        (item) => item.type === DriverTermsAndConditionsType.PrivacyPolicy,
    );

    const handleTermsAndConditionClick = useCallback(() => {
        setSelectedTermsType(DriverTermsAndConditionsType.TermsAndConditions);
    }, []);

    const handlePrivacyPolicyClick = useCallback(() => {
        setSelectedTermsType(DriverTermsAndConditionsType.PrivacyPolicy);
    }, []);

    const handleModalClose = useCallback(() => {
        setSelectedTermsType(null);
    }, []);

    const handleSubmit = useCallback(async () => {
        if (!termsAndConditionsForAcceptance) {
            throw new Error("Terms and conditions not found");
        }

        const { termsAndConditions, privacyPolicy } = termsAndConditionsForAcceptance;

        const termsIds: string[] = [];
        if (termsAndConditions) {
            termsIds.push(termsAndConditions._id);
        }
        if (privacyPolicy) {
            termsIds.push(privacyPolicy._id);
        }
        if (termsIds.length === 0) {
            throw new Error("No terms and conditions to accept");
        }

        await acceptTermsAndConditions(termsIds);
        await refetch();
    }, [acceptTermsAndConditions, refetch, termsAndConditionsForAcceptance]);

    if (!termsAndConditionsForAcceptance?.termsAndConditions && !termsAndConditionsForAcceptance?.privacyPolicy) {
        return null;
    }

    let headlineText = "Accept our Terms & Review Privacy policy";
    let text = (
        <>
            By tapping “Yes, I agree”, you confirm that you have read and agree to Daytrip’s{" "}
            <span className="cursor-pointer text-blue-700 underline" onClick={handleTermsAndConditionClick}>
                Terms and Conditions
            </span>{" "}
            and{" "}
            <span className="cursor-pointer text-blue-700 underline" onClick={handlePrivacyPolicyClick}>
                Driver Privacy Policy
            </span>
            .
        </>
    );
    if (hasTermsAndConditionsChangelog && hasPrivacyPolicyChangelog) {
        headlineText = "Terms and Privacy Policy updates";
        text = (
            <>
                We’ve made some updates to our{" "}
                <span className="cursor-pointer text-blue-700 underline" onClick={handleTermsAndConditionClick}>
                    Terms & Conditions
                </span>{" "}
                and{" "}
                <span className="cursor-pointer text-blue-700 underline" onClick={handlePrivacyPolicyClick}>
                    Privacy Policy
                </span>
                . Please review each change before continuing.
            </>
        );
    } else if (hasTermsAndConditionsChangelog) {
        headlineText = "Terms & Conditions updates";
        text = (
            <>
                We’ve made some updates to our{" "}
                <span className="cursor-pointer text-blue-700 underline" onClick={handleTermsAndConditionClick}>
                    Terms & Conditions
                </span>
                . Please review each change before continuing.
            </>
        );
    } else if (hasPrivacyPolicyChangelog) {
        headlineText = "Privacy Policy updates";
        text = (
            <>
                We’ve made some updates to our{" "}
                <span className="cursor-pointer text-blue-700 underline" onClick={handlePrivacyPolicyClick}>
                    Privacy Policy
                </span>
                . Please review each change before continuing.
            </>
        );
    }

    return (
        <>
            <div className="fixed bg-white top-0 left-0 w-full h-full z-10">
                <Container className="flex flex-col items-center justify-center h-full gap-6">
                    <Headline className="m-0" rank={2}>
                        {headlineText}
                    </Headline>
                    <div>{text}</div>
                    <Button onClick={handleSubmit} isLoading={isLoading} data-cy="acceptTermsAndConditions">
                        {hasTermsAndConditionsChangelog || hasPrivacyPolicyChangelog
                            ? "Agree and continue"
                            : "Yes, I agree"}
                    </Button>
                </Container>
            </div>
            <DriverTermsAndConditionsModal type={selectedTermsType} onClose={handleModalClose} />
        </>
    );
});
